// NOTE(jxom): WIP design rev of `app-ref/index`

import { Dialog as BaseDialog } from '@base-ui/react/dialog'
import { Tabs as BaseTabs } from '@base-ui/react/tabs'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router'
import type { ComponentType, ReactElement } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Amount, Box, Button, Empty, Hash, Info, Qr, Rows, cx, formatHash, useCopy } from 'regen-ui'
import { toast } from 'sonner'
import { formatUnits, maxUint256 } from 'viem'
import { useConnection } from 'wagmi'
import { tempo } from 'wagmi/chains'
import ArrowDownLeft from '~icons/lucide/arrow-down-left'
import ArrowDownToLine from '~icons/lucide/arrow-down-to-line'
import ArrowLeftRight from '~icons/lucide/arrow-left-right'
import ArrowUpRight from '~icons/lucide/arrow-up-right'
import Check from '~icons/lucide/check'
import CopyIcon from '~icons/lucide/copy'
import Plus from '~icons/lucide/plus'
import ReceiptText from '~icons/lucide/receipt-text'
import ScanQrCode from '~icons/lucide/scan-qr-code'
import SendHorizontal from '~icons/lucide/send-horizontal'
import ShieldCheck from '~icons/lucide/shield-check'
import Wallet from '~icons/lucide/wallet'
import X from '~icons/lucide/x'

import * as Activity from '#/lib/activity.js'
import { checksumAddressForCopy } from '#/lib/address.js'
import * as Assets from '#/lib/assets.js'
import { useAssets } from '#/lib/assets.js'
import * as Auth from '#/lib/auth.js'
import * as Bridge from '#/lib/bridge.js'
import { wagmiConfig } from '#/lib/config.js'
import { useDialogConnector } from '#/lib/connectors.js'
import { readOnly } from '#/lib/connectors/readOnly.js'
import { formatCreditCents, useCreditsBalance } from '#/lib/credits.js'
import * as Currency from '#/lib/currency.js'
import { useFundingOptions } from '#/lib/funding-options.js'
import * as Valuation from '#/lib/valuation.js'
import { BalancesCard } from '#/routes/_app/-components/BalancesCard.js'
import { ScreenLayout } from '#/routes/_app/-components/ScreenLayout.js'
import { DepositCryptoContent } from '#/routes/remote/-frames/transaction/DepositCrypto.js'
import type { ActivityEntry, ActivityItem } from '../../../worker/lib/activity.js'

export const Route = createFileRoute('/_app/')({
  component: HomePage,
  head: () => ({
    links: [{ href: 'https://wallet.tempo.xyz/', rel: 'canonical' }],
    scripts: [
      {
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@id': 'https://wallet.tempo.xyz/#application',
          '@type': 'WebApplication',
          applicationCategory: 'FinanceApplication',
          description: 'Your wallet for Tempo balances, transfers, deposits, and account control.',
          image: 'https://wallet.tempo.xyz/og-image.png',
          isPartOf: { '@id': 'https://tempo.xyz/#website' },
          name: 'Tempo Wallet',
          operatingSystem: 'Web',
          publisher: { '@id': 'https://tempo.xyz/#organization' },
          url: 'https://wallet.tempo.xyz/',
        }),
        type: 'application/ld+json',
      },
    ],
  }),
})

const money = new Intl.NumberFormat('en-US', {
  currency: 'USD',
  maximumFractionDigits: 2,
  style: 'currency',
})

const relative = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })

function HomePage() {
  const me = Auth.me.useQuery()
  const connection = useConnection()
  const { address: connectedAddress, chainId } = connection
  const isViewOnly = connection.connector?.type === readOnly.type
  const address = isViewOnly ? connectedAddress : (me.data?.address ?? connectedAddress)
  // Hint tokens drive on-chain `fresh` reads on the worker and ensure
  // each token has a row in the cache (worker keeps 0-balance rows for
  // hints). On testnet the faucet drops 4 USD stables, so seed all of
  // them — that way `setReceiptData` has rows to mutate when receipts
  // come back, not just PathUSD.
  const hintTokens =
    chainId === tempo.id
      ? (['0x20C000000000000000000000b9537d11c60E8b50'] as const)
      : ([
          '0x20c0000000000000000000000000000000000000', // PathUSD
          '0x20c0000000000000000000000000000000000001', // AlphaUSD
          '0x20c0000000000000000000000000000000000002', // BetaUSD
          '0x20c0000000000000000000000000000000000003', // ThetaUSD
        ] as const)
  // `useAssets` forwards the persisted zone auth token (when unlocked)
  // and the worker decodes the zone to also fetch — so the dashboard
  // gets a concatenated list with both public and private holdings,
  // each row tagged with `zoneId`. BalancesCard groups by `(symbol,
  // address)` so the pair collapses into one card row.
  const assets = useAssets({
    account: address,
    chainId,
    enabled: typeof window !== 'undefined' && !!address,
    fresh: true,
    tokens: hintTokens,
  })
  const valuation = Valuation.useValuation({
    account: address,
    chainId,
    enabled: typeof window !== 'undefined' && !!address,
  })
  const fundingOptions = useFundingOptions()
  const creditsEnabled = !!address && chainId === tempo.id && fundingOptions.data?.coinflow === true
  const credits = useCreditsBalance({ enabled: creditsEnabled })
  const queryClient = useQueryClient()

  const total = useMemo(
    () =>
      Valuation.total({
        assets: assets.data ?? [],
        fallback: (assets.data ?? []).reduce((sum, asset) => {
          if (Currency.inferCurrency(asset.symbol) !== 'USD') return sum
          const zoneSum = (asset.zones ?? []).reduce((s, z) => s + BigInt(z.balance), 0n)
          const gross = BigInt(asset.balance) + zoneSum
          return sum + Number(formatUnits(gross, asset.decimals))
        }, 0),
        valuation: valuation.data,
      }),
    [assets.data, valuation.data?.amount, valuation.data?.available],
  )

  const { copy, notifying: copied } = useCopy()
  const [receiveOpen, setReceiveOpen] = useState(false)

  // Auto-open the deposit dialog when navigated to with `?action=fund`,
  // then strip the param so it doesn't re-trigger on subsequent renders.
  // Wait for `address` so the dialog connector has a session synced from webAuthn.
  const search = useSearch({ strict: false }) as { action?: string; intent?: string }
  const navigate = useNavigate()
  const connector = useDialogConnector()
  const deposit = useMutation({
    mutationFn: async () => {
      const provider = await connector.getProvider()
      return (await provider.request({
        method: 'wallet_deposit',
        params: [getDepositParams(search.intent)],
      })) as { receipts?: readonly { logs: readonly Assets.setReceiptData.Log[] }[] } | undefined
    },
    onSuccess: (data) => {
      toast.success('Funds deposited')
      if (address && chainId && data?.receipts)
        for (const receipt of data.receipts)
          Assets.setReceiptData({ account: address, chainId, queryClient, receipt })
      if (address) queryClient.invalidateQueries({ queryKey: Activity.queryKey(address) })
    },
  })

  const send = useMutation({
    mutationFn: async () => {
      const provider = await connector.getProvider()
      return (await provider.request({
        method: 'wallet_transfer',
        params: [{ editable: true }],
      } as never)) as { chainId: number; receipt: { logs: readonly Assets.setReceiptData.Log[] } }
    },
    onSuccess: (result) => {
      toast.success('Transaction confirmed.')
      if (address)
        Assets.setReceiptData({
          account: address,
          chainId: result.chainId,
          queryClient,
          receipt: result.receipt,
        })
      if (address) queryClient.invalidateQueries({ queryKey: Activity.queryKey(address) })
    },
  })

  const swap = useMutation({
    mutationFn: async () => {
      const provider = await connector.getProvider()
      await provider.request({
        method: 'wallet_swap',
        params: [{ type: 'sell' }],
      } as never)
    },
    onSuccess: () => {
      toast.success('Swap confirmed.')
      if (address) {
        queryClient.invalidateQueries({ queryKey: Assets.queryKey(address) })
        queryClient.invalidateQueries({ queryKey: Activity.queryKey(address) })
      }
    },
  })

  const triggered = useRef(false)
  useEffect(() => {
    if (search.action !== 'fund' || isViewOnly || !address || triggered.current) return
    triggered.current = true
    navigate({
      to: '/',
      search: (prev) => ({ ...prev, action: undefined, intent: undefined }),
      replace: true,
    })
    deposit.mutate()
  }, [search.action, isViewOnly, address])

  return (
    <ScreenLayout walletActions={false}>
      {address && chainId ? (
        <div className="flex flex-col gap-4">
          <WalletSummary
            copied={copied}
            creditsCents={credits.data?.credits.cents ?? 0}
            creditsEnabled={creditsEnabled}
            depositing={deposit.isPending}
            loading={assets.isLoading}
            onCopyAddress={() => copy(checksumAddressForCopy(address))}
            onDeposit={() => deposit.mutate()}
            onReceive={() => setReceiveOpen(true)}
            onSend={() => send.mutate()}
            onSwap={() => swap.mutate()}
            sending={send.isPending}
            swapping={swap.isPending}
            total={total}
          />
          <BalancesCard
            assets={assets.data ?? []}
            chainId={chainId}
            emptyAction={
              <div className="flex gap-2">
                <Button
                  icon={<Plus className="size-4" />}
                  loading={deposit.isPending}
                  onClick={() => deposit.mutate()}
                  size="small"
                  variant="secondary"
                >
                  Deposit
                </Button>
                <Button
                  icon={<ScanQrCode className="size-4" />}
                  onClick={() => setReceiveOpen(true)}
                  size="small"
                  variant="secondary"
                >
                  Receive
                </Button>
              </div>
            }
            loading={assets.isLoading}
          />
          <RecentActivityCard address={address} chainId={chainId} />
          <ReceiveDialog
            address={address}
            chainId={chainId}
            onOpenChange={setReceiveOpen}
            open={receiveOpen}
          />
        </div>
      ) : (
        <Box className="max-sm:-mx-4 sm:-mx-8 md:mx-0 max-md:rounded-none max-md:border-x-0">
          <Empty icon={<Wallet />} title="No wallet connected">
            Sign in to view balances and activity.
          </Empty>
        </Box>
      )}
      <LearnMore />
    </ScreenLayout>
  )
}

function getDepositParams(intent: string | undefined): {
  intent?: 'applePay' | 'credits' | 'crypto' | 'faucet' | 'referralCode' | undefined
} {
  switch (intent) {
    case 'applePay':
    case 'credits':
    case 'crypto':
    case 'faucet':
    case 'referralCode':
      return { intent }
    default:
      return {}
  }
}

function WalletSummary(props: {
  copied: boolean
  creditsCents: number
  creditsEnabled: boolean
  depositing: boolean
  loading: boolean
  onCopyAddress: () => void
  onDeposit: () => void
  onReceive: () => void
  onSend: () => void
  onSwap: () => void
  sending: boolean
  swapping: boolean
  total: number
}) {
  const {
    copied,
    creditsCents,
    creditsEnabled,
    depositing,
    loading,
    onCopyAddress,
    onDeposit,
    onReceive,
    onSend,
    onSwap,
    sending,
    swapping,
    total,
  } = props

  return (
    <section className="flex min-w-0 flex-col gap-4 py-2">
      <div className="flex min-w-0 flex-col gap-1">
        <p className="text-label-13 text-foreground-secondary">Total balance</p>
        <p
          className="text-heading-40 tabular-nums text-foreground"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {loading ? (
            <span className="inline-block h-[1em] w-48 animate-pulse rounded-body bg-skeleton align-middle" />
          ) : (
            money.format(total)
          )}
        </p>
        {creditsEnabled && creditsCents > 0 && (
          <p className="text-label-13 text-foreground-secondary">
            {formatCreditCents(creditsCents)} MPP Credits
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 min-[520px]:flex min-[520px]:flex-wrap">
        <WalletAction
          icon={Plus}
          label="Deposit"
          loading={depositing}
          onClick={onDeposit}
          variant="primary"
        />
        <WalletAction icon={ScanQrCode} label="Receive" onClick={onReceive} />
        <WalletAction icon={SendHorizontal} label="Send" loading={sending} onClick={onSend} />
        <WalletAction icon={ArrowLeftRight} label="Swap" loading={swapping} onClick={onSwap} />
        <WalletAction
          className="max-[519px]:hidden"
          icon={copied ? Check : CopyIcon}
          label={copied ? 'Copied' : 'Copy address'}
          onClick={onCopyAddress}
        />
      </div>
    </section>
  )
}

function WalletAction(props: {
  children?: string | undefined
  className?: string | undefined
  icon: ComponentType<{ className?: string | undefined }>
  label: string
  loading?: boolean | undefined
  onClick?: (() => void) | undefined
  render?: ReactElement | undefined
  variant?: 'primary' | 'secondary' | undefined
}) {
  const {
    children,
    className,
    icon: Icon,
    label,
    loading = false,
    onClick,
    render,
    variant = 'secondary',
  } = props

  return (
    <Button
      className={cx('h-9 w-full min-w-0 justify-center px-3 min-[520px]:w-auto', className)}
      icon={<Icon className="size-4" />}
      loading={loading}
      onClick={onClick}
      render={render}
      size="small"
      type="button"
      variant={variant}
    >
      {children ?? label}
    </Button>
  )
}

function ReceiveDialog(props: {
  address: string
  chainId: number
  onOpenChange: (open: boolean) => void
  open: boolean
}) {
  const { address, chainId, onOpenChange, open } = props
  const { copy, notifying: copied } = useCopy()
  const [mode, setMode] = useState<'network' | 'tempo'>('tempo')
  const checksumAddress = checksumAddressForCopy(address)
  const network = getNetworkName(chainId)
  const qrValue = `ethereum:${checksumAddress}@${chainId}`

  useEffect(() => {
    if (!open) setMode('tempo')
  }, [open])

  return (
    <BaseDialog.Root onOpenChange={onOpenChange} open={open}>
      <BaseDialog.Portal>
        <BaseDialog.Backdrop className="fixed inset-0 z-40 bg-background/80" />
        <BaseDialog.Popup
          className={cx(
            'animate-dialog-card fixed top-1/2 left-1/2 z-50 flex w-[min(calc(100vw-32px),420px)] -translate-x-1/2 -translate-y-1/2 flex-col gap-5 rounded-body border border-border bg-surface p-5 shadow-lg focus-visible:outline-none',
            'max-sm:inset-x-0 max-sm:top-auto max-sm:bottom-0 max-sm:w-full max-sm:translate-x-0 max-sm:translate-y-0',
            'max-sm:rounded-b-none max-sm:border-x-0 max-sm:border-b-0',
          )}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 flex-col gap-1">
              <BaseDialog.Title className="text-heading-24 text-foreground">
                Receive
              </BaseDialog.Title>
              <BaseDialog.Description className="text-copy-15 text-foreground-secondary">
                Choose how you want to receive funds.
              </BaseDialog.Description>
            </div>
            <BaseDialog.Close
              className={cx(
                'inline-flex size-8 shrink-0 items-center justify-center rounded-button text-foreground-secondary transition-colors',
                'hover:bg-surface-hover hover:text-foreground-secondary-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
              )}
            >
              <X aria-hidden="true" className="size-4" />
              <span className="sr-only">Close</span>
            </BaseDialog.Close>
          </div>

          <BaseTabs.Root
            onValueChange={(value) => setMode(value as 'network' | 'tempo')}
            value={mode}
          >
            <BaseTabs.List className="relative flex w-full rounded-button bg-secondary p-1">
              <BaseTabs.Indicator
                className="absolute top-1 bottom-1 left-0 rounded-button bg-surface transition-all duration-200 ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none"
                style={{
                  translate: 'var(--active-tab-left)',
                  width: 'var(--active-tab-width)',
                }}
              />
              <BaseTabs.Tab
                className="relative z-10 flex-1 cursor-pointer rounded-button py-1.5 text-label-13 text-foreground-secondary transition-colors hover:text-foreground data-[active]:text-foreground"
                value="tempo"
              >
                Tempo
              </BaseTabs.Tab>
              <BaseTabs.Tab
                className="relative z-10 flex-1 cursor-pointer rounded-button py-1.5 text-label-13 text-foreground-secondary transition-colors hover:text-foreground data-[active]:text-foreground"
                value="network"
              >
                Other network
              </BaseTabs.Tab>
            </BaseTabs.List>
          </BaseTabs.Root>

          {mode === 'network' ? (
            <DepositCryptoContent address={address} networks={Bridge.sourceChains} />
          ) : (
            <>
              <div className="flex flex-col items-center gap-4 rounded-body bg-pane px-4 py-5">
                <Qr size="large" value={qrValue} />
              </div>

              <div className="flex flex-col gap-2">
                <p className="text-label-13 text-foreground-secondary">Wallet address</p>
                <div className="flex items-center gap-2 rounded-body border border-border bg-surface px-3 py-2">
                  <Hash
                    className="min-w-0 flex-1 text-label-14 text-foreground"
                    value={checksumAddress}
                  />
                  <Button
                    icon={copied ? <Check className="size-4" /> : <CopyIcon className="size-4" />}
                    onClick={() => copy(checksumAddress)}
                    size="small"
                    type="button"
                    variant="secondary"
                  >
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              </div>

              <Info
                message={`Only receive supported assets on ${network}. Sending assets on another network may result in permanent loss.`}
                type="warning"
              />
            </>
          )}
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  )
}

function getNetworkName(chainId: number) {
  const name = wagmiConfig.chains.find((chain) => chain.id === chainId)?.name ?? `Chain ${chainId}`
  if (name === 'Tempo Mainnet') return 'Tempo'
  return name
}

const learnMoreLinks = [
  {
    description: 'A purpose built blockchain for fast and reliable payments.',
    href: 'https://tempo.xyz',
    title: 'Tempo Blockchain',
  },
  {
    description: 'SDKs, APIs, and guides to build on Tempo.',
    href: 'https://docs.tempo.xyz',
    title: 'Developer Tools',
  },
  {
    description: 'Learn how agents can make and accept payments.',
    href: 'https://mpp.dev',
    title: 'Machine Payments Protocol',
  },
  {
    description: 'Private balances and transfers, now live on testnet.',
    href: 'https://tempo.xyz/blog/introducing-tempo-zones',
    title: 'Tempo Zones',
  },
] as const

function LearnMore() {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-4">
        <h2 className="shrink-0 text-label-14 text-foreground-secondary">Learn more</h2>
        <div className="h-0 flex-1 border-t border-border" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {learnMoreLinks.map((link) => (
          <Box
            className="group flex flex-col max-sm:-mx-4 max-sm:rounded-none max-sm:border-x-0"
            key={link.href}
            render={<a href={link.href} rel="noopener noreferrer" target="_blank" />}
          >
            <div className="flex flex-1 flex-col gap-2">
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-label-14 font-medium text-foreground">{link.title}</h3>
                <ArrowUpRight className="size-4 shrink-0 text-foreground-secondary group-hover:text-foreground" />
              </div>
              <p className="text-label-13 text-foreground-secondary">{link.description}</p>
            </div>
          </Box>
        ))}
      </div>
    </div>
  )
}

function RecentActivityCard(props: { address: string; chainId: number }) {
  const { address, chainId } = props
  const activity = Activity.useActivity({ account: address as `0x${string}`, chainId })

  const rows = useMemo(() => {
    const entries = activity.data?.pages.flatMap((p) => p.items) ?? []
    return entries.flatMap(toRecentActivityRows).slice(0, 4)
  }, [activity.data])

  return (
    <Box
      className="relative flex min-h-[286px] flex-col max-sm:-mx-4 sm:-mx-8 md:mx-0 max-md:rounded-none max-md:border-x-0"
      padding={0}
    >
      <div className="flex items-center justify-between px-5 pt-5 pb-3">
        <h2 className="text-label-16 font-medium text-foreground">Activity</h2>
        <Button.Text
          className="-mr-1.5"
          render={<Link search={(prev: Record<string, unknown>) => prev} to="/activity" />}
        >
          View all
        </Button.Text>
      </div>
      <Rows className="pb-5 [&_[data-rows-divider]]:hidden" variant="surface">
        {activity.isLoading ? (
          <>
            <ActivitySkeletonRow />
            <ActivitySkeletonRow />
            <ActivitySkeletonRow />
          </>
        ) : rows.length === 0 ? (
          <Empty
            className="pointer-events-none absolute inset-0 min-h-0 px-5 py-0"
            icon={<ReceiptText />}
            title="No activity yet"
          >
            Transactions, deposits, swaps, and app authorizations will appear here.
          </Empty>
        ) : (
          rows.map((row) => <RecentActivityRow key={row.id} row={row} />)
        )}
      </Rows>
    </Box>
  )
}

type RecentActivityRow =
  | { entry: Extract<ActivityEntry, { type: 'group' }>; id: string; kind: 'group' }
  | { id: string; item: ActivityItem; kind: 'item' }

function toRecentActivityRows(entry: ActivityEntry): RecentActivityRow[] {
  if (entry.type === 'group')
    return [{ entry, id: `group-${entry.signer}-${entry.timestamp}`, kind: 'group' }]
  return [{ id: entry.hash, item: entry, kind: 'item' }]
}

function RecentActivityRow(props: { row: RecentActivityRow }) {
  const { row } = props
  if (row.kind === 'group')
    return (
      <Rows.Row height={64}>
        <span className="flex size-6 shrink-0 items-center justify-center rounded-identicon bg-secondary text-foreground-secondary">
          <ReceiptText className="size-3.5" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-label-14 text-foreground">Access key activity</span>
          <span className="truncate text-label-13 text-foreground-secondary">
            Access key {formatHash(row.entry.signer)}
          </span>
        </span>
        <span className="flex shrink-0 flex-col items-end">
          <span className="text-label-14 text-foreground">{row.entry.items.length} txns</span>
          <span className="text-label-13 text-foreground-secondary tabular-nums">
            {formatRelativeTime(row.entry.timestamp)}
          </span>
        </span>
      </Rows.Row>
    )

  const subtitle = activitySubtitle(row.item)
  return (
    <Rows.Row height={64}>
      <RecentActivityIcon type={row.item.type} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-label-14 text-foreground">{activityTitle(row.item)}</span>
        {subtitle && (
          <span className="truncate text-label-13 text-foreground-secondary">{subtitle}</span>
        )}
      </span>
      <span className="flex shrink-0 flex-col items-end">
        <RecentActivityAmount item={row.item} />
        <span className="text-label-13 text-foreground-secondary tabular-nums">
          {formatRelativeTime(row.item.timestamp)}
        </span>
      </span>
    </Rows.Row>
  )
}

const activityIcons: Record<string, ComponentType<{ className?: string | undefined }>> = {
  approval: ShieldCheck,
  burn: ArrowUpRight,
  deposit: ArrowDownToLine,
  mint: ArrowDownToLine,
  received: ArrowDownLeft,
  send: ArrowUpRight,
  swap: ArrowLeftRight,
  withdrawal: ArrowDownLeft,
}

function RecentActivityIcon(props: { type: string }) {
  const Icon = activityIcons[props.type] ?? ReceiptText
  return (
    <span className="flex size-6 shrink-0 items-center justify-center rounded-identicon bg-secondary text-foreground-secondary">
      <Icon className="size-3.5" />
    </span>
  )
}

function activityTitle(item: ActivityItem) {
  const service = item.serviceName
  switch (item.type) {
    case 'send':
      return `${service ?? 'Sent'} ${item.amount.symbol}`
    case 'received':
      return `${service ?? 'Received'} ${item.amount.symbol}`
    case 'mint':
      return `${service ?? 'Deposited'} ${item.amount.symbol}`
    case 'burn':
      return `${service ?? 'Withdrew'} ${item.amount.symbol}`
    case 'deposit':
      return `${service ?? 'Deposited'} ${item.amount.symbol}`
    case 'withdrawal':
      return `${service ?? 'Withdrew'} ${item.amount.symbol}`
    case 'swap':
      return `${service ?? 'Swapped'} ${item.amountIn.symbol} for ${item.amountOut.symbol}`
    case 'approval':
      return `${service ?? 'Authorized'} ${item.amount.symbol}`
    case 'session-closed':
      return service ?? 'Session closed'
    case 'access-key-created':
      return service ?? 'Access key created'
    case 'access-key-revoked':
      return service ?? 'Access key revoked'
    case 'token-created':
      return `${service ?? 'Token created'} ${item.symbol}`
  }
}

function activitySubtitle(item: ActivityItem) {
  switch (item.type) {
    case 'send':
      return `to ${formatHash(item.to)}`
    case 'received':
      return `from ${formatHash(item.from)}`
    case 'mint':
      return undefined
    case 'burn':
      return undefined
    case 'deposit':
      return `to ${formatHash(item.vault)}`
    case 'withdrawal':
      return `from ${formatHash(item.vault)}`
    case 'swap':
      return undefined
    case 'approval':
      return `spender ${formatHash(item.spender)}`
    case 'session-closed':
      if (item.refundAmount)
        return `${item.refundAmount.formatted} ${item.refundAmount.symbol} refunded`
      return undefined
    case 'access-key-created':
      return formatHash(item.publicKey)
    case 'access-key-revoked':
      return formatHash(item.publicKey)
    case 'token-created':
      return undefined
  }
}

function RecentActivityAmount(props: { item: ActivityItem }) {
  const { item } = props
  const className = 'max-sm:max-w-[min(36vw,150px)] sm:min-w-max sm:max-w-none'
  switch (item.type) {
    case 'send':
      return <Amount align="right" amount={item.amount} className={className} sign="−" size="md" />
    case 'received':
      return <Amount align="right" amount={item.amount} className={className} sign="+" size="md" />
    case 'mint':
      return <Amount align="right" amount={item.amount} className={className} sign="+" size="md" />
    case 'burn':
      return <Amount align="right" amount={item.amount} className={className} sign="−" size="md" />
    case 'deposit':
      return <Amount align="right" amount={item.amount} className={className} sign="−" size="md" />
    case 'withdrawal':
      return <Amount align="right" amount={item.amount} className={className} sign="+" size="md" />
    case 'swap':
      return <Amount align="right" amount={item.amountOut} className={className} size="md" />
    case 'approval':
      if (BigInt(item.amount.amount) >= maxUint256 / 2n)
        return <span className="text-label-14 text-foreground-secondary">Unlimited</span>
      return <Amount align="right" amount={item.amount} className={className} size="md" />
    case 'session-closed':
      if (item.refundAmount)
        return (
          <Amount
            align="right"
            amount={item.refundAmount}
            className={className}
            sign="+"
            size="md"
          />
        )
      return null
    default:
      return null
  }
}

function ActivitySkeletonRow() {
  return (
    <Rows.Row height={64}>
      <div className="size-6 animate-pulse rounded-identicon bg-skeleton" />
      <div className="flex flex-1 flex-col gap-1">
        <div className="h-3.5 w-24 animate-pulse rounded-body bg-skeleton" />
        <div className="h-3 w-16 animate-pulse rounded-body bg-skeleton" />
      </div>
      <div className="h-3.5 w-16 animate-pulse rounded-body bg-skeleton" />
    </Rows.Row>
  )
}

function formatRelativeTime(timestamp: number): string {
  const diff = timestamp - Date.now()
  const absDiff = Math.abs(diff)
  if (absDiff < 60_000) return 'just now'
  if (absDiff < 3_600_000) return relative.format(Math.round(diff / 60_000), 'minute')
  if (absDiff < 86_400_000) return relative.format(Math.round(diff / 3_600_000), 'hour')
  if (absDiff < 604_800_000) return relative.format(Math.round(diff / 86_400_000), 'day')
  return new Date(timestamp).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}
